// @flow

import * as React from 'react';
import IntersectionObserver from 'app/packages/intersection-observer-component';
import PropTypes from 'prop-types';
import shallowEqual from 'app/packages/shallow-equal';
import TimedPair from 'app/packages/time-spent/TimedPair';

interface TimeSpentManager {
  registerComponent(component: TimeSpent): void; // eslint-disable-line no-use-before-define
  unregisterComponent(component: TimeSpent): void; // eslint-disable-line no-use-before-define
}

type ViewType = $Values<PCONST.thrift.ViewType>;
type ViewParameterType = $Values<PCONST.thrift.ViewParameterType>;
type ComponentType = $Values<PCONST.thrift.ComponentType>;
type ElementType = $Values<PCONST.thrift.ElementType>;

export type TimeSpentProps = {
  children?: React.Element<any>,
  view?: ViewType,
  viewParameter?: ?ViewParameterType,
  component?: ComponentType,
  element?: ElementType,
  objectId?: string,
  clientTrackingParams?: string,
  viewData?: Object,
  auxData?: Object,
};

type Props = TimeSpentProps & {
  timeSpentManager: TimeSpentManager,
};

type State = {|
  timedPair: ?TimedPair,
|};

type Context = {|
  view: number,
  viewParameter: number,
  component: number,
  element: number,
  objectId: string,
  clientTrackingParams?: string,
  getParentUuid: Function,
|};

class TimeSpent extends React.Component<Props, State> {
  static contextTypes = {
    view: PropTypes.number,
    viewParameter: PropTypes.number,
    component: PropTypes.number,
    element: PropTypes.number,
    objectId: PropTypes.string,
    clientTrackingParams: PropTypes.string,
    getParentUuid: PropTypes.func,
  };

  static childContextTypes = {
    view: PropTypes.number,
    viewParameter: PropTypes.number,
    component: PropTypes.number,
    element: PropTypes.number,
    objectId: PropTypes.string,
    clientTrackingParams: PropTypes.string,
    getParentUuid: PropTypes.func,
  };

  constructor(props: Props, context: Context) {
    super(props, context);
    this.state = {
      timedPair: new TimedPair(this.getLoggerContext()),
    };
  }

  getChildContext = () => {
    const { view, viewParameter, component, element, objectId, clientTrackingParams } = this.props;

    // The object that is returned when a child component calls this.context.
    // Return the value that is in the prop; if it is null, return the value
    // provided by an ancestor TimeSpent component.
    return {
      view: view || this.context.view,
      viewParameter: viewParameter || this.context.viewParameter,
      component: component || this.context.component,
      element,
      objectId: objectId || this.context.objectId,
      clientTrackingParams: clientTrackingParams || this.context.clientTrackingParams,
      // Return a reference to this component's getUuid. In some cases, shouldComponentUpdate
      // will return false, and we can't count on the context updating.
      // Should've just used redux from the get-go...
      getParentUuid: () => this.getUuid(),
    };
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (!this.isRegistered) {
      return;
    }

    if (
      this.props.view !== nextProps.view ||
      this.props.viewParameter !== nextProps.viewParameter ||
      this.props.component !== nextProps.component ||
      this.props.element !== nextProps.element ||
      this.props.objectId !== nextProps.objectId ||
      !shallowEqual(this.props.auxData, nextProps.auxData) ||
      !shallowEqual(this.props.viewData, nextProps.viewData)
    ) {
      // if something changed, unregister the current component and re-register the new one
      // this is mostly when transitioning between pages (i.e. Search results,
      // clicking on a SearchGuide) that don't cause an unmount
      this.unregisterComponent();
      setTimeout(this.registerComponent);
    }
  }

  componentWillUnmount = () => {
    this.unregisterComponent();
  };

  // Helper method to getChildContext, and excludes the parent ID.
  // Parent ID is not a property on ContextEvent, but rather submitted
  // in auxData.
  getLoggerContext = () => {
    const {
      view,
      viewParameter,
      component,
      element,
      objectId,
      clientTrackingParams,
    } = this.getChildContext();

    return {
      view,
      viewParameter,
      component,
      element,
      objectId,
      clientTrackingParams,
    };
  };

  getParentId = () => {
    const parentId = this.context.getParentUuid ? this.context.getParentUuid() : undefined;
    return parentId;
  };

  getUuid = () => {
    if (this.state.timedPair) {
      return this.state.timedPair.uuid;
    }
    return undefined;
  };

  getTimedPair = () => this.state.timedPair;

  setTimedPair = (timedPair: ?TimedPair) => {
    this.setState({
      timedPair,
    });
  };

  isRegistered: boolean = false;

  registerComponent = () => {
    this.props.timeSpentManager.registerComponent(this);
    this.isRegistered = true;
  };

  unregisterComponent = () => {
    if (this.isRegistered) {
      this.props.timeSpentManager.unregisterComponent(this);
      this.isRegistered = false;
    }
  };

  handleVisibilityChange: IntersectionObserverCallback = (entries) => {
    const { intersectionRatio } = entries[0];
    const { bottom, left, right, top } = entries[0].intersectionRect;

    if (intersectionRatio > 0) {
      this.registerComponent();
    } else if (intersectionRatio === 0) {
      // Need to check all dimensions of the intersectionRect, since intersectionRatio
      // may be 0 when top === bottom or left === right
      if (bottom === 0 && left === 0 && right === 0 && top === 0) {
        this.unregisterComponent();
      } else {
        this.registerComponent();
      }
    }
  };

  render() {
    return (
      <IntersectionObserver
        options={{ threshold: 0 }}
        onVisibilityChange={this.handleVisibilityChange}
      >
        {React.Children.only(this.props.children)}
      </IntersectionObserver>
    );
  }
}

export default TimeSpent;
